import React, { useState, useEffect } from "react";
import axios from "axios";

function App() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [text, setText] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const bucket = process.env.REACT_APP_BUCKET_NAME;
  const apiUrl = process.env.REACT_APP_API_URL;
  const region = process.env.REACT_APP_REGION;
  const webexClientId = process.env.REACT_APP_WEBEX_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_WEBEX_REDIRECT_URI;

  // Check for authentication on component mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    setLoading(true);
    
    // Check if we're coming back from OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      // Check if we've already processed this code (prevent double-processing)
      const processedCode = sessionStorage.getItem('processed_oauth_code');
      
      if (processedCode === code) {
        console.log('[DEBUG] Code already processed, skipping');
        setLoading(false);
        return;
      }
      
      // Mark this code as being processed
      sessionStorage.setItem('processed_oauth_code', code);
      
      // Exchange code for token
      await handleOAuthCallback(code);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Check if we have a stored token
    const storedToken = localStorage.getItem('webex_access_token');
    const storedUser = localStorage.getItem('webex_user');
    
    if (storedToken && storedUser) {
      try {
        // Verify token is still valid
        const response = await axios.post(`${apiUrl}/auth/verify`, {
          access_token: storedToken
        });
        
        if (response.data.valid) {
          setAuthenticated(true);
          setUser(JSON.parse(storedUser));
        } else {
          // Token invalid, clear storage
          localStorage.removeItem('webex_access_token');
          localStorage.removeItem('webex_user');
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        localStorage.removeItem('webex_access_token');
        localStorage.removeItem('webex_user');
      }
    }
    
    setLoading(false);
  };

  const handleOAuthCallback = async (code) => {
    try {
      const response = await axios.get(`${apiUrl}/auth/callback`, {
        params: { code }
      });
      
      const { access_token, user: userData } = response.data;
      
      // Store token and user info
      localStorage.setItem('webex_access_token', access_token);
      localStorage.setItem('webex_user', JSON.stringify(userData));
      
      setAuthenticated(true);
      setUser(userData);
      setError("");
    } catch (err) {
      console.error('OAuth callback error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Authentication failed';
      setError(errorMessage);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    const authUrl = `https://webexapis.com/v1/authorize?client_id=${webexClientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&scope=spark:people_read`;
    window.location.href = authUrl;
  };

  const handleLogout = () => {
    localStorage.removeItem('webex_access_token');
    localStorage.removeItem('webex_user');
    sessionStorage.removeItem('processed_oauth_code');
    setAuthenticated(false);
    setUser(null);
    setText("");
    setFile(null);
    setError("");
  };

  const handleUpload = async () => {
    if (!file) return alert("Choose a file first.");

    setUploading(true);
    setError("");

    try {
      console.log('[DEBUG] Step 1: Requesting presigned URL...');
      // Ask backend for presigned URL
      const presignRes = await axios.post(`${apiUrl}/get-upload-url`, {
        filename: file.name,
        filetype: file.type,
      });
      console.log('[DEBUG] Presigned URL response:', presignRes.data);

      const { uploadUrl, key } = presignRes.data;

      console.log('[DEBUG] Step 2: Uploading to S3...');
      console.log('[DEBUG] Upload URL:', uploadUrl);
      // Upload directly to S3 using the presigned URL
      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
      });
      console.log('[DEBUG] Upload to S3 successful');

      setUploading(false);
      setExtracting(true);

      console.log('[DEBUG] Step 3: Calling /extract endpoint...');
      console.log('[DEBUG] Extract URL:', `${apiUrl}/extract`);
      console.log('[DEBUG] Key:', key);
      // Call /extract to run Textract
      const extractRes = await axios.post(`${apiUrl}/extract`, { key });
      console.log('[DEBUG] Extract response:', extractRes.data);

      setText(extractRes.data.text);
    } catch (err) {
      console.error('[ERROR] Upload/Extract failed:', err);
      console.error('[ERROR] Error message:', err.message);
      console.error('[ERROR] Error response:', err.response);
      
      let errorMessage = "Upload or OCR failed. ";
      if (err.response) {
        errorMessage += `Server error: ${err.response.status} - ${err.response.data?.error || err.response.statusText}`;
      } else if (err.request) {
        errorMessage += "No response from server. Check CORS or network.";
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    }

    setExtracting(false);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h1>Image to Text Converter</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <h1 style={styles.title}>Image to Text Converter</h1>
          <p style={styles.subtitle}>
            Powered by AWS Textract and secured with Webex OAuth
          </p>
          
          {error && (
            <div style={styles.errorBox}>
              <strong>Error:</strong> {error}
            </div>
          )}
          
          <button onClick={handleLogin} style={styles.loginButton}>
            <svg style={styles.icon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Login with Webex
          </button>
          
          <p style={styles.notice}>
            Only @cisco.com email addresses are authorized
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.appTitle}>Image to Text Converter</h1>
          <p style={styles.welcomeText}>Welcome, {user?.name} ({user?.email})</p>
        </div>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      <div style={styles.uploadSection}>
        <label style={styles.fileInputLabel}>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files[0])}
            style={styles.fileInput}
          />
          <span style={styles.fileInputButton}>
            Choose Image File
          </span>
        </label>

        {file && (
          <p style={styles.fileName}>
            Selected: <strong>{file.name}</strong>
          </p>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || uploading || extracting}
          style={{
            ...styles.uploadButton,
            ...((!file || uploading || extracting) && styles.uploadButtonDisabled)
          }}
        >
          {uploading
            ? "Uploading..."
            : extracting
            ? "Extracting text..."
            : "Upload & Extract Text"}
        </button>
      </div>

      {text && (
        <div style={styles.resultSection}>
          <h3 style={styles.resultTitle}>Extracted Text:</h3>
          <textarea
            style={styles.textarea}
            value={text}
            readOnly
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(text);
              alert('Text copied to clipboard!');
            }}
            style={styles.copyButton}
          >
            Copy to Clipboard
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "40px",
    fontFamily: "Arial, sans-serif",
    maxWidth: "1000px",
    margin: "0 auto",
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  loginCard: {
    backgroundColor: "white",
    padding: "60px 40px",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    textAlign: "center",
    maxWidth: "500px",
    margin: "100px auto",
  },
  title: {
    color: "#333",
    marginBottom: "10px",
    fontSize: "28px",
  },
  subtitle: {
    color: "#666",
    marginBottom: "40px",
    fontSize: "16px",
  },
  loginButton: {
    backgroundColor: "#0D6EFD",
    color: "white",
    border: "none",
    padding: "16px 32px",
    fontSize: "18px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    transition: "background-color 0.2s",
    fontWeight: "600",
  },
  icon: {
    width: "24px",
    height: "24px",
  },
  notice: {
    marginTop: "30px",
    color: "#666",
    fontSize: "14px",
  },
  header: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  appTitle: {
    margin: "0 0 5px 0",
    fontSize: "24px",
    color: "#333",
  },
  welcomeText: {
    margin: 0,
    color: "#666",
    fontSize: "14px",
  },
  logoutButton: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  uploadSection: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    marginBottom: "30px",
  },
  fileInputLabel: {
    display: "block",
    marginBottom: "20px",
  },
  fileInput: {
    display: "none",
  },
  fileInputButton: {
    display: "inline-block",
    backgroundColor: "#6c757d",
    color: "white",
    padding: "12px 24px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
  },
  fileName: {
    marginBottom: "20px",
    color: "#333",
    fontSize: "14px",
  },
  uploadButton: {
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    padding: "14px 28px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    width: "100%",
  },
  uploadButtonDisabled: {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
  },
  resultSection: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  resultTitle: {
    marginTop: 0,
    marginBottom: "15px",
    color: "#333",
    fontSize: "20px",
  },
  textarea: {
    width: "100%",
    height: "300px",
    padding: "15px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    fontFamily: "monospace",
    resize: "vertical",
    marginBottom: "15px",
  },
  copyButton: {
    backgroundColor: "#0D6EFD",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  errorBox: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #f5c6cb",
  },
};

export default App;
